import {
  Heading,
  Text,
  useMediaQuery,
  theme,
  Container,
  Box,
} from '@everlywell/ui-kit';
import Carousel from 'components/Carousel';
import GridCard, { GridCardProps } from 'components/GridCard';
import VideoGridCard, { VideoGridCardProps } from 'components/VideoGridCard';
import React from 'react';

// Types ----------

export interface MediaGridCardListProps {
  cards: (GridCardProps | VideoGridCardProps)[];
  eyebrow: string;
  title: string;
}

// Helper function to check if a card is a video card
const isVideoCard = (
  card: GridCardProps | VideoGridCardProps,
): card is VideoGridCardProps => 'video' in card;

// MediaGridCardList ------

const MediaGridCardList: React.FC<MediaGridCardListProps | null> = (props) => {
  const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.tablet})`);

  if (!props) {
    return null;
  }

  const { cards, eyebrow, title } = props;

  const GridCards = cards.map((card, index: number) => {
    if (isVideoCard(card)) {
      return <VideoGridCard key={`video-card-${index}`} {...card} />;
    } else {
      return <GridCard key={`card-${index}`} {...card} />;
    }
  });

  const oddNumberOfCards = GridCards.length % 2 !== 0;

  return (
    <Container size={['sm', 'sm', 'xl', 'xl', 'xl']} padding="0">
      <Box
        display="flex"
        flexDirection={['column', 'row']}
        gap={[6, 6, 5]}
        maxWidth={['100vw', '100%']}
      >
        <Box width={[1, '33.333%']}>
          <Text
            size="xl"
            marginBottom={['1', '2']}
            color={theme.colors.viridian.base}
            textAlign={['center', 'left']}
          >
            {eyebrow}
          </Text>

          <Heading
            // @ts-ignore
            size={['2xl', '2xl']}
            textAlign={['center', 'left']}
          >
            {title}
          </Heading>
        </Box>

        <Box width={[1, '66.667%']}>
          {isMobile ? (
            <Box>
              <Carousel
                spaceBetween="24px"
                slidesPerView="auto"
                pagination={GridCards.length > 1}
                showPagination={true}
                slideStyle={{
                  minWidth: 280,
                  width: 280,
                  maxWidth: 280,
                }}
              >
                {cards.map((card, index) => {
                  const mobileImageStyles = {
                    width: '280px !important',
                    height: '180px !important',
                    minWidth: 280,
                  };
                  return (
                    <Box minWidth="280" key={`grid-card-${index}`}>
                      {isVideoCard(card) ? (
                        <VideoGridCard
                          key={`video-grid-card-${index}`}
                          {...card}
                          imageStyles={mobileImageStyles}
                        />
                      ) : (
                        <GridCard
                          key={`grid-card-${index}`}
                          {...card}
                          imageStyles={mobileImageStyles}
                        />
                      )}
                    </Box>
                  );
                })}
              </Carousel>
            </Box>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns="repeat(2, 1fr)"
              gap="20px"
              justifyContent="start"
              maxWidth="100%"
              sx={{
                '@media (max-width: 1000px)': {
                  gridTemplateColumns: '1fr',
                },
                '@media (min-width: 1001px)': {
                  gridTemplateColumns: 'repeat(2, 1fr)',
                },
              }}
            >
              {cards.map((card, index) => {
                const firstCardFullWidth = oddNumberOfCards && index === 0;
                const imageStyles = {
                  ...(firstCardFullWidth
                    ? { aspectRatio: 2.5, width: '100%' }
                    : {}),
                };

                if (isVideoCard(card)) {
                  return (
                    <Box
                      key={`video-grid-card-${index}`}
                      width="100%"
                      marginBottom={64}
                    >
                      <VideoGridCard {...card} imageStyles={imageStyles} />
                    </Box>
                  );
                } else {
                  return (
                    <Box
                      key={`grid-card-${index}`}
                      width="100%"
                      marginBottom={64}
                    >
                      <GridCard {...card} imageStyles={imageStyles} />
                    </Box>
                  );
                }
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

// Export ---------

export default MediaGridCardList;
